import { BaseTable, ColumnProp } from 'common/components/table/base';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import {
  ProviderOutstanding,
  ProviderOutstandingsActions,
} from 'common/state/provider/actions';
import {
  ProviderOutstandingsState,
  defaultProviderOutstandingsState,
} from 'common/state/provider/states';
import { doGetProviderOutstandings } from 'common/state/provider/thunkActions';
import { providerOutstandingsRecipe } from 'common/state/provider/recipes';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Currency } from 'common/classes/currency';
import {
  FilterProp,
  FiltersContainer,
  FilterTypes,
  useFiltration,
} from 'common/components/table/filtering';
import { mdiBell } from '@mdi/js';
import { TableTitle } from 'common/components/tables';
import { Button } from '@mollybet/ui';
import styled from 'styled-components';

const GroupByButtons = styled.div`
  display: flex;
  align-items: center;

  > * {
    margin-right: 0.5rem;
  }
`;

const GroupByTitle = styled.span`
  font-size: 0.75rem;
  text-transform: uppercase;
  font-weight: bold;
`;

const FilterGroup = styled.div`
  display: flex;
  margin-top: 1rem;
`;

export const ProviderOutstandingTable = () => {
  const [state, dispatch] = useProducerWithThunks<
    ProviderOutstandingsState,
    ProviderOutstandingsActions
  >(providerOutstandingsRecipe, defaultProviderOutstandingsState);
  const [groupSystem, setGroupSystem] = useState(false);
  const [groupAccount, setGroupAccount] = useState(false);
  const [groupStream, setGroupStream] = useState(false);
  const dropdownRef = useRef();

  const { providerOutstandings } = state;

  useEffect(() => {
    dispatch(
      doGetProviderOutstandings({
        group_system: groupSystem ? 1 : 0,
        group_account: groupAccount ? 1 : 0,
        group_stream: groupStream ? 1 : 0,
      })
    );
  }, [dispatch, groupSystem, groupAccount, groupStream]);

  const columns = useMemo(
    (): ColumnProp<ProviderOutstanding>[] => [
      { key: 'provider_username', title: 'Provider Username' },
      { key: 'account_username', title: 'Account Username' },
      { key: 'system', title: 'System' },
      { key: 'stream_name', title: 'Stream' },
      {
        key: 'amount',
        title: 'Amount',
        valueFormatter: (row: ProviderOutstanding) =>
          new Currency(row.amount, row.currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.amount, row.currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: ProviderOutstanding) =>
          parseFloat(row.amount),
      },
    ],
    []
  );

  const options = useMemo(() => {
    const seenProviders = new Set<number>();
    const seenAccounts = new Set<number>();
    const seenSystems = new Set<string>();
    const seenStreams = new Set<string>();
    const providers = [{ label: 'All', value: undefined }];
    const accounts = [{ label: 'All', value: undefined }];
    const systems = [{ label: 'All', value: undefined }];
    const streams = [{ label: 'All', value: undefined }];

    if (providerOutstandings?.length) {
      providerOutstandings.forEach((providerOutstanding) => {
        if (
          providerOutstanding.provider_id != null &&
          !seenProviders.has(providerOutstanding.provider_id)
        ) {
          seenProviders.add(providerOutstanding.provider_id);
          providers.push({
            label: providerOutstanding.provider_username,
            value: providerOutstanding.provider_id,
          });
        }
        if (
          providerOutstanding.account_id != null &&
          !seenAccounts.has(providerOutstanding.account_id)
        ) {
          seenAccounts.add(providerOutstanding.account_id);
          accounts.push({
            label: providerOutstanding.account_username,
            value: providerOutstanding.account_id,
          });
        }
        if (
          providerOutstanding.system != null &&
          !seenSystems.has(providerOutstanding.system)
        ) {
          seenSystems.add(providerOutstanding.system);
          systems.push({
            label: providerOutstanding.system_name,
            value: providerOutstanding.system,
          });
        }
        if (
          providerOutstanding.stream != null &&
          !seenStreams.has(providerOutstanding.stream)
        ) {
          seenStreams.add(providerOutstanding.stream);
          streams.push({
            label: providerOutstanding.stream_name,
            value: providerOutstanding.stream,
          });
        }
      });
    }

    return { providers, accounts, systems, streams };
  }, [providerOutstandings]);

  const filters = useMemo((): FilterProp<ProviderOutstanding>[] => {
    return [
      {
        key: 'amount',
        label: 'Amount',
        type: FilterTypes.NUMBER,
        valueGenerator: (row: ProviderOutstanding) => parseFloat(row.amount),
      },
      {
        key: 'provider_id',
        label: 'Provider',
        type: FilterTypes.OPTIONS,
        options: options.providers,
        inputProps: { menuPortalTarget: dropdownRef.current },
      },
      {
        key: 'account_id',
        label: 'Account',
        type: FilterTypes.OPTIONS,
        options: options.accounts,
        inputProps: { menuPortalTarget: dropdownRef.current },
      },
      {
        key: 'system',
        label: 'System',
        type: FilterTypes.OPTIONS,
        options: options.systems,
        inputProps: { menuPortalTarget: dropdownRef.current },
      },
      {
        key: 'stream',
        label: 'Streams',
        type: FilterTypes.OPTIONS,
        options: options.streams,
        inputProps: { menuPortalTarget: dropdownRef.current },
      },
    ];
  }, [options]);

  const [rows, filterComponents, filterHooks] = useFiltration(
    providerOutstandings || [],
    filters
  );

  return (
    <>
      {' '}
      <TableTitle icon={mdiBell} title="Outstanding" />
      <GroupByButtons>
        <GroupByTitle>Group By:</GroupByTitle>
        <Button
          variant={groupSystem ? 'primary' : 'secondary'}
          onClick={() => setGroupSystem(!groupSystem)}
        >
          System
        </Button>
        <Button
          variant={groupAccount ? 'primary' : 'secondary'}
          onClick={() => setGroupAccount(!groupAccount)}
        >
          Account
        </Button>
        <Button
          variant={groupStream ? 'primary' : 'secondary'}
          onClick={() => setGroupStream(!groupStream)}
        >
          Stream
        </Button>
      </GroupByButtons>
      <br />
      <FiltersContainer hooks={filterHooks}>
        <FilterGroup>{filterComponents.amount}</FilterGroup>
        <FilterGroup>
          {filterComponents.provider_id}
          {filterComponents.account_id}
          {filterComponents.system}
          {filterComponents.stream}
        </FilterGroup>
      </FiltersContainer>
      <div ref={dropdownRef} />
      <br />
      <BaseTable
        columns={columns}
        rows={rows}
        csvName="Oustanding"
        isLoading={state.pending}
        error={state.error}
      />
    </>
  );
};
